import { CustomerDisplayType, GetDeviceConfigurationResponse, Res } from 'core/api';
import { Settings } from './Settings';
import * as Cordova from './Cordova';
import Electron from './Electron';

type Platform = {
	type: 'Electron' | 'Cordova' | 'Web',
	canUseCardProcessor: boolean,
	canUseCashControl: boolean,
	canUseCustomerDisplay: boolean,
	canUseFingerprint: boolean,
	canUseScale: boolean,
	shouldIgnoreLicenseCheck: () => Promise<boolean>
}

const electron: Platform = {
	type: 'Electron',
	canUseCardProcessor: true,
	canUseCashControl: true,
	canUseCustomerDisplay: true,
	canUseFingerprint: true,
	canUseScale: true,
	shouldIgnoreLicenseCheck: () => Promise.resolve(false)
};

const cordova: Platform = {
	type: 'Cordova',
	canUseCardProcessor: true,
	canUseCashControl: false,
	canUseCustomerDisplay: false,
	canUseFingerprint: false,
	canUseScale: false,
	shouldIgnoreLicenseCheck: Cordova.shouldIgnoreLicenseCheck
};

const web: Platform = {
	type: 'Web',
	canUseCardProcessor: false,
	canUseCashControl: false,
	canUseCustomerDisplay: false,
	canUseFingerprint: false,
	canUseScale: false,
	shouldIgnoreLicenseCheck: () => Promise.resolve(false)
};

const current: Platform = Cordova.isCordovaNavigator() ? cordova : Electron.isEnabled() ? electron : web;

export async function configure(configuration: GetDeviceConfigurationResponse, settings: Settings, startCashControl: () => void): Promise<void> {

	const result = validate(configuration);
	if (!result.isValid)
		throw new Error(result.errors.join('\r\n'));

	if (Cordova.isCordovaNavigator())
		await Cordova.configure(configuration, settings);

	if (Electron.isEnabled())
		await Electron.configure(configuration, startCashControl);
}

export function validate(configuration: GetDeviceConfigurationResponse): { isValid: true } | { isValid: false, errors: string[] } {

	const errors: string[] = [];

	if (configuration.UseCardProcessor && !current.canUseCardProcessor)
		errors.push(Res.Msg.MSG_PLATFORM_DOES_NOT_SUPPORT_CARD_PROCESSOR());

	if (configuration.UseCashControl && !current.canUseCashControl)
		errors.push(Res.Msg.MSG_PLATFORM_DOES_NOT_SUPPORT_CASH_CONTROL());

	if (configuration.CustomerDisplayConfiguration.DisplayType !== CustomerDisplayType.None && !current.canUseCustomerDisplay)
		errors.push(Res.Msg.MSG_PLATFORM_DOES_NOT_SUPPORT_CUSTOMER_DISPLAY());

	if (configuration.UseFingerprintReader && !current.canUseFingerprint)
		errors.push(Res.Msg.MSG_PLATFORM_DOES_NOT_SUPPORT_FINGER_PRINT_READER());

	if (configuration.UseScale && !current.canUseScale)
		errors.push(Res.Msg.MSG_PLATFORM_DOES_NOT_SUPPORT_SCALE());

	return errors.length ? { isValid: false, errors } : { isValid: true };
}

export function getType() {
	return current.type;
}

export function canUseScale() {
	return current.canUseScale;
}

export function canUseCardProcessor() {
	return current.canUseCardProcessor;
}

export function canUseCashControl() {
	return current.canUseCashControl;
}

export function canUseFingerprint() {
	return current.canUseCashControl;
}

export async function shouldIgnoreLicenseCheck() {
	return current.shouldIgnoreLicenseCheck();
}
